import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { exportDataset, fetchDataset } from "../../data/queries/datasets";
import { Dataset, DatasetItem } from "../../types/dataset";
import { logError } from "../../utils/error";
import { DatasetItemDetailDialog } from "./components/DatasetItemDetailDialog";
import { useDatasetItems } from "./hooks/useDatasetItems";

export const DatasetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DatasetItem | null>(null);

  // Memoize the pagination object to prevent infinite re-renders
  const paginationParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage
  }), [page, rowsPerPage]);

  const {
    items,
    paginatedData,
    loading: itemsLoading,
    deleteItem,
    deleteItems
  } = useDatasetItems({
    datasetId: id || "",
    pagination: paginationParams // Use the memoized object
  });

  useEffect(() => {
    const loadDataset = async () => {
      if (!id) {
        setError("Dataset ID not found");
        setLoading(false);
        return;
      }

      try {
        const result = await fetchDataset(id);
        if (result.success && result.data) {
          setDataset(result.data);
          setEditName(result.data.dataset_name);
          setEditDescription(result.data.dataset_description);
        } else {
          setError(result.error || "Failed to load dataset");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dataset");
      } finally {
        setLoading(false);
      }
    };

    loadDataset();
  }, [id]);

  const handleExport = async () => {
    if (!id) {
      return;
    }

    try {
      const result = await exportDataset(id);
      if (result.success && result.data) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], {
          type: "application/json"
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `dataset-${dataset?.dataset_name || "export"}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      logError("Export failed", err);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    const success = await deleteItem(itemId);
    if (success) {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      return;
    }

    const success = await deleteItems(selectedItems);
    if (success) {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleViewItem = (item: DatasetItem) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getAnnotationTypeColor = (type: string) => {
    switch (type) {
      case "benchmark": return "primary";
      case "issue": return "error";
      case "score": return "success";
      case "routing": return "info";
      case "missing_lens": return "warning";
      default: return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dataset) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Dataset not found"}</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/datasets")}
          sx={{ mt: 2 }}
        >
          Back to Datasets
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/datasets")}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {dataset.dataset_name}
        </Typography>
        <Button
          startIcon={<ExportIcon />}
          onClick={handleExport}
          variant="outlined"
        >
          Export
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            {isEditing ? (
              <Box>
                <TextField
                  fullWidth
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {dataset.dataset_name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {dataset.dataset_description}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={() => setIsEditing(!isEditing)}>
            <EditIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Items</Typography>
            <Typography variant="h6">{dataset.dataset_item_count}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Created by</Typography>
            <Typography variant="body1">{dataset.created_by}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Created</Typography>
            <Typography variant="body1">{formatDate(dataset.created_at)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Updated</Typography>
            <Typography variant="body1">{formatDate(dataset.updated_at)}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper>
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Dataset Items</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {selectedItems.length > 0 && (
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                onClick={handleBulkDelete}
                disabled={itemsLoading}
              >
                Delete Selected ({selectedItems.length})
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
                    checked={items.length > 0 && selectedItems.length === items.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Interaction</TableCell>
                <TableCell>Artifact Type</TableCell>
                <TableCell>Annotation Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsLoading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No items in this dataset. Add items from the Explorer.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`/explore?interaction=${item.interaction_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.interaction_id.slice(0, 8)}...
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.artifact_type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.annotation_type}
                        size="small"
                        color={getAnnotationTypeColor(item.annotation_type) as any}
                      />
                    </TableCell>
                    <TableCell>{item.annotation_category}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {item.annotation_description}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.created_by}</TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => handleViewItem(item)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete item">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteItem(item.id)}
                          disabled={itemsLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginatedData && (
          <TablePagination
            component="div"
            count={paginatedData.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </Paper>

      <DatasetItemDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        item={selectedItem}
      />
    </Box>
  );
};
